// File: src/contexts/UserContext.jsx
// Production-ready notification system with proper error handling

import React, { createContext, useState, useEffect, useContext } from 'react';
import { ordersAPI, productsAPI, chatAPI } from '../services/api';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState({
    orders: [],
    products: [],
    reviews: [],
    chats: []
  });
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (err) {
        console.error('Failed to parse stored user:', err);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setIsInitialized(true);
  }, []);

  // Update user and handle chat migration
  const updateUser = async (newUser) => {
    if (newUser) {
      const previousUser = user;
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);

      if (!previousUser && newUser) {
        await handleLoginMigration();
      }
    } else {
      handleLogout();
    }
  };

  // Handle login migration
  const handleLoginMigration = async () => {
    const guestToken = localStorage.getItem('chat_guest_token');
    if (!guestToken) return;

    try {
      await chatAPI.migrateGuest({ guestToken });
      localStorage.removeItem('chat_guest_token');
    } catch (err) {
      console.error('Chat migration failed:', err);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('chat_convo_id');
    
    setUser(null);
    setNotifications({
      orders: [],
      products: [],
      reviews: [],
      chats: []
    });
  };

  const logout = () => {
    handleLogout();
  };

  // Clear a specific notification section
  const clearSection = (sectionType) => {
    setNotifications(prev => ({
      ...prev,
      [sectionType]: []
    }));
  };

  // Mark section as read (clears it, calls API for orders)
  const markSectionAsRead = async (sectionType) => {
    try {
      if (sectionType === 'orders') {
        const orderIds = notifications[sectionType].map(n => n.id.split('-')[1]);
        await Promise.all(
          orderIds.map(id => ordersAPI.markNotificationViewed(id).catch(err => {
            console.error(`Failed to mark order ${id} as viewed:`, err);
          }))
        );
      }
      clearSection(sectionType);
    } catch (err) {
      console.error(`Failed to mark ${sectionType} as read:`, err);
      clearSection(sectionType);
    }
  };

  // Fetch admin notifications - GROUPED BY SECTION
  const fetchNotifications = async () => {
    if (!user?.isAdmin) {
      setNotifications({
        orders: [],
        products: [],
        reviews: [],
        chats: []
      });
      return;
    }

    setLoadingNotifications(true);
    const newNotifications = {
      orders: [],
      products: [],
      reviews: [],
      chats: []
    };

    try {
      // Fetch unviewed orders
      try {
        const { data: orders } = await ordersAPI.getUnviewed();
        newNotifications.orders = orders.map(order => {
          const productNames = (order.items || [])
            .map(item => item.name || 'Article inconnu')
            .slice(0, 2)
            .join(', ');
          const moreItems = order.items?.length > 2 ? ` +${order.items.length - 2} de plus` : '';
          
          const subtotal = (order.items || [])
            .reduce((sum, item) => sum + (parseFloat(item.price || 0) * (item.quantity || 0)), 0);
          const total = Math.max(0, subtotal - Number(order.discount_amount || 0)).toFixed(2);
          
          return {
            id: `order-${order.id}`,
            type: 'orders',
            message: `Nouvelle commande #${order.id}`,
            details: `${productNames}${moreItems} - Total : DT ${total}${order.promo_code ? ` (${order.promo_code})` : ""}`,
            timestamp: new Date(order.createdAt).toLocaleString('fr-FR'),
            link: `/admin/orders/${order.id}`,
            priority: 'high',
            rawDate: new Date(order.createdAt)
          };
        }).sort((a, b) => b.rawDate - a.rawDate);
      } catch (err) {
        console.error('Failed to fetch order notifications:', err);
      }

      // Fetch low stock products
      try {
        const { data: products } = await productsAPI.getLowStock();
        newNotifications.products = products.map(product => ({
          id: `product-${product.id}`,
          type: 'products',
          message: 'Alerte stock faible',
          details: `${product.name} • Plus que ${product.stock} en stock (Seuil : ${product.lowStockThreshold || 5})`,
          timestamp: new Date().toLocaleString('fr-FR'),
          link: '/admin/products',
          priority: product.stock === 0 ? 'high' : 'medium',
          rawDate: new Date()
        })).sort((a, b) => {
          if (a.priority === 'high' && b.priority !== 'high') return -1;
          if (a.priority !== 'high' && b.priority === 'high') return 1;
          return 0;
        });
      } catch (err) {
        console.error('Failed to fetch product notifications:', err);
      }

      // Fetch recent reviews (last 30 minutes)
      try {
        const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
        const { data: recentReviewsData } = await productsAPI.getAdminReviews({
          sort: 'createdAt',
          limit: 20
        });
        
        newNotifications.reviews = recentReviewsData.reviews
          .filter(r => new Date(r.createdAt) > thirtyMinutesAgo)
          .map(review => ({
            id: `review-${review.id}`,
            type: 'reviews',
            message: 'Nouvel avis',
            details: `${review.productName} - ${review.rating} étoiles par ${review.firstName} ${review.lastName}`,
            timestamp: new Date(review.createdAt).toLocaleString('fr-FR'),
            link: `/admin/reviews?productId=${review.productId}`,
            priority: 'low',
            rawDate: new Date(review.createdAt)
          }))
          .sort((a, b) => b.rawDate - a.rawDate);
      } catch (err) {
        console.error('Failed to fetch review notifications:', err);
      }

      // Fetch active chat conversations
      try {
        const { data: conversations } = await chatAPI.getActiveConversations();
        newNotifications.chats = conversations
          .filter(c => c.unreadCount > 0)
          .map(convo => {
            const userName = convo.user_email 
              ? `${convo.firstName || ''} ${convo.lastName || ''}`.trim() || convo.user_email
              : 'Invité';
            
            return {
              id: `chat-${convo.id}`,
              type: 'chats',
              message: 'Message assistance',
              details: `${convo.unreadCount} non lus de ${userName} - ${convo.subject || "Assistance générale"}`,
              timestamp: new Date(convo.updated_at).toLocaleString('fr-FR'),
              link: `/admin/chats/${convo.id}`,
              priority: 'high',
              rawDate: new Date(convo.updated_at)
            };
          })
          .sort((a, b) => b.rawDate - a.rawDate);
      } catch (err) {
        console.error('Failed to fetch chat notifications:', err);
      }

      setNotifications(newNotifications);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setNotifications({
        orders: [],
        products: [],
        reviews: [],
        chats: []
      });
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Get total count across all sections
  const getTotalNotifications = () => {
    return Object.values(notifications).reduce((sum, section) => sum + section.length, 0);
  };

  // Mark all sections as read
  const markAllAsRead = async () => {
    const sections = ['orders', 'products', 'reviews', 'chats'];
    await Promise.all(
      sections.map(section => markSectionAsRead(section).catch(err => {
        console.error(`Failed to mark ${section} as read:`, err);
      }))
    );
  };

  // Fetch notifications on user change and periodically
  useEffect(() => {
    if (!isInitialized) return;
    
    fetchNotifications();
    
    if (user?.isAdmin) {
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isInitialized]);

  return (
    <UserContext.Provider 
      value={{ 
        user, 
        updateUser, 
        logout, 
        notifications, 
        loadingNotifications, 
        fetchNotifications,
        isInitialized,
        getTotalNotifications,
        clearSection,
        markSectionAsRead,
        markAllAsRead
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
